import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChevronDown, 
  Play, 
  Pause, 
  RotateCcw, 
  Repeat,
  Loader2,
  Volume2,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { useMeditation, logMeditationStart, updateMeditationProgress } from '@/hooks/useMeditations';
import { useCategoryTracks } from '@/hooks/useCategoryTracks';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useMeditationPlayerStore } from '@/store/meditationPlayerStore';

export function RelaxPlayer() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const { meditation, loading } = useMeditation(slug);
  const { tracks } = useCategoryTracks(meditation?.category_id);
  
  const {
    isPlaying,
    currentTime,
    duration,
    historyId,
    loop,
    setHistoryId,
    setLoop,
    setCurrentMeditation,
    play,
    pause,
    seek,
    skipBack: storeSkipBack,
    skipForward: storeSkipForward,
  } = useMeditationPlayerStore();
  
  // Текущий индекс в плейлисте + соседние треки
  const currentIndex = tracks.findIndex((t) => t.slug === slug);
  const prevTrack = currentIndex > 0 ? tracks[currentIndex - 1] : null;
  const nextTrack = currentIndex >= 0 && currentIndex < tracks.length - 1 
    ? tracks[currentIndex + 1] 
    : null;

  // Загружаем медитацию в глобальный плеер при смене slug
  // (если это новая медитация — не та что уже играет)
  useEffect(() => {
    if (!meditation) return;
    const store = useMeditationPlayerStore.getState();
    if (store.currentMeditation?.id !== meditation.id) {
      setCurrentMeditation(meditation);
      setHistoryId(null);
    }
  }, [meditation?.id]);
  
  // Логирование начала прослушивания
  useEffect(() => {
    if (!meditation || !profile?.id) return;
    if (historyId) return;
    (async () => {
      const id = await logMeditationStart(profile.id, meditation.id);
      if (id) setHistoryId(id);
    })();
  }, [meditation?.id, profile?.id, historyId]);
  
  // 🆕 НЕ останавливаем аудио при размонтировании — пусть продолжает играть.
  // Сохраняем только финальный прогресс.
  useEffect(() => {
    return () => {
      const store = useMeditationPlayerStore.getState();
      if (store.historyId && store.currentTime > 5) {
        const completed = store.currentTime >= store.duration * 0.9;
        updateMeditationProgress(store.historyId, store.currentTime, completed);
      }
      // НЕ вызываем reset() — аудио должно продолжать играть
    };
  }, []);
  
  function togglePlay() {
    haptic('medium');
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }
  
  function handleSkipBack() {
    storeSkipBack(15);
    haptic('light');
  }
  
  function handleSkipForward() {
    storeSkipForward(15);
    haptic('light');
  }
  
  function toggleLoop() {
    haptic('light');
    setLoop(!loop);
  }
  
  // 🆕 Кнопка "Назад" — закрываем полный плеер, музыка продолжает играть (mini-player покажется)
  function handleClose() {
    haptic('light');
    // Возвращаемся в Релакс таб напрямую
    nav('/chat?tab=relax');
  }
  
  function handleSeek(e: React.ChangeEvent<HTMLInputElement>) {
    const newTime = Number(e.target.value);
    seek(newTime);
  }
  
  // Переключение на следующий/предыдущий трек
  function goToTrack(targetSlug: string) {
    // Сохраняем прогресс текущего трека
    if (historyId && currentTime > 5) {
      const completed = currentTime >= duration * 0.9;
      updateMeditationProgress(historyId, currentTime, completed);
    }
    
    haptic('medium');
    nav(`/relax/player/${targetSlug}`, { replace: true });
  }
  
  function goToPrev() {
    if (!prevTrack) return;
    goToTrack(prevTrack.slug);
  }
  
  function goToNext() {
    if (!nextTrack) return;
    goToTrack(nextTrack.slug);
  }
  
  if (loading || !meditation) {
    return (
      <div className="flex flex-col h-[100dvh] items-center justify-center bg-gradient-to-b from-bg-card to-bg">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
      </div>
    );
  }
  
  const isShort = meditation.duration_seconds < 90;
  const positionInPlaylist = currentIndex >= 0 ? `${currentIndex + 1} из ${tracks.length}` : '';
  
  return (
    <div className="flex flex-col h-[100dvh] bg-gradient-to-b from-bg-card via-bg to-bg max-w-md mx-auto">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <button
          onClick={handleClose}
          className="w-9 h-9 rounded-full bg-bg-elevated flex items-center justify-center"
        >
          <ChevronDown size={18} className="text-ink-muted" />
        </button>
        <div className="text-[10px] text-ink-dim uppercase tracking-wider text-center flex-1 px-2">
          <div>{meditation.category?.title}</div>
          {positionInPlaylist && (
            <div className="text-[9px] mt-0.5 normal-case tracking-normal text-emerald-glow/60">
              {positionInPlaylist}
            </div>
          )}
        </div>
        <div className="w-9 h-9" />
      </div>
      
      {/* Cover */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        <motion.div
          key={meditation.id}
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className={`w-52 h-52 rounded-3xl flex items-center justify-center mb-8 shadow-2xl shadow-emerald-glow/20 transition-transform ${
            isPlaying ? 'animate-pulse-slow' : ''
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(74,222,128,0.3), rgba(56,189,248,0.15))',
          }}
        >
          <div className="text-7xl">{meditation.emoji || '🎵'}</div>
        </motion.div>
        
        {/* Title */}
        <div className="text-center mb-6">
          <div className="font-display font-bold text-xl mb-1">{meditation.title}</div>
          {meditation.subtitle && (
            <div className="text-xs text-ink-muted">{meditation.subtitle}</div>
          )}
        </div>
        
        {/* Description */}
        {meditation.description && (
          <div className="bg-bg-card/60 border border-line rounded-xl p-3 mb-6 max-w-xs">
            <div className="text-[11px] text-ink-muted leading-relaxed text-center">
              💡 {meditation.description}
            </div>
          </div>
        )}
        
        {/* Progress */}
        <div className="w-full max-w-xs mb-4">
          <input
            type="range"
            min={0}
            max={duration || meditation.duration_seconds}
            step={1}
            value={currentTime}
            onChange={handleSeek}
            
            className="w-full accent-emerald-glow cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-ink-dim mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration || meditation.duration_seconds)}</span>
          </div>
        </div>
        
        {/* Расширенные контролы */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={goToPrev}
            disabled={!prevTrack}
            className="w-10 h-10 rounded-full flex items-center justify-center text-ink-muted active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Предыдущий трек"
          >
            <SkipBack size={18} className="fill-current" />
          </button>
          
          {!isShort && (
            <button
              onClick={handleSkipBack}
              
              className="w-10 h-10 rounded-full bg-bg-elevated flex items-center justify-center text-ink-muted active:scale-95 transition-all "
              aria-label="Назад 15 сек"
            >
              <RotateCcw size={16} />
            </button>
          )}
          
          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-emerald-glow text-bg flex items-center justify-center active:scale-95 transition-all shadow-lg shadow-emerald-glow/30"
            aria-label={isPlaying ? 'Пауза' : 'Играть'}
          >
            {isPlaying ? (
              <Pause size={24} className="fill-current" />
            ) : (
              <Play size={24} className="fill-current ml-0.5" />
            )}
          </button>
          
          {!isShort && (
            <button
              onClick={handleSkipForward}
              
              className="w-10 h-10 rounded-full bg-bg-elevated flex items-center justify-center text-ink-muted active:scale-95 transition-all "
              aria-label="Вперёд 15 сек"
            >
              <RotateCcw size={16} className="-scale-x-100" />
            </button>
          )}
          
          <button
            onClick={goToNext}
            disabled={!nextTrack}
            className="w-10 h-10 rounded-full flex items-center justify-center text-ink-muted active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Следующий трек"
          >
            <SkipForward size={18} className="fill-current" />
          </button>
        </div>
        
        {/* Цикл */}
        <button
          onClick={toggleLoop}
          className={`text-[11px] flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
            loop 
              ? 'bg-emerald-glow/15 text-emerald-glow' 
              : 'text-ink-dim hover:bg-bg-elevated'
          }`}
        >
          <Repeat size={12} />
          {loop ? 'Повтор включён' : 'Повторять'}
        </button>
      </div>
      
      {/* Подпись со следующим треком */}
      {nextTrack && !loop && (
        <div className="px-8 pb-3 text-center">
          <div className="text-[10px] text-ink-dim">
            Дальше: <span className="text-ink-muted">{nextTrack.emoji} {nextTrack.title}</span>
          </div>
        </div>
      )}
      
      {/* Сноска */}
      <div className="px-8 pb-6 text-center">
        <div className="text-[10px] text-ink-dim flex items-center justify-center gap-1">
          <Volume2 size={10} />
          В наушниках будет глубже
        </div>
      </div>
    </div>
  );
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
